import React from 'react';
import { Form, FormGroup, Input } from 'reactstrap';
import { connect } from 'react-redux';
import { 
  setImageQuality, 
  setViewportWidth, 
  toggleFullPage,
  setScreenshotFormat,
  setScreenshotSelector,
  setExcludeSelectors,
  toggleBlockCookieBanners,
  toggleDarkMode
} from '../../../../redux/actions/debuggerForm';

const ToggleSwitch = ({ id, checked, onChange, label, disabled }) => (
  <div className="toggle-row">
    <label className="toggle-label" htmlFor={id}>{label}</label>
    <label className="switch">
      <input id={id} type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span className="slider round"></span>
    </label>
  </div>
);

const RadioButtonGroup = ({ options, value, onChange, name, disabledOptions = [] }) => (
  <div className="radio-group">
    {options.map(opt => {
      const isSelected = String(value).trim().toLowerCase() === String(opt.value).trim().toLowerCase();
      return (
        <button
          key={opt.value}
          type="button"
          className={`radio-btn${isSelected ? ' selected' : ''}${disabledOptions.includes(opt.value) ? ' disabled' : ''}`}
          onClick={() => !disabledOptions.includes(opt.value) && onChange(opt.value)}
          disabled={disabledOptions.includes(opt.value)}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const InfoIcon = ({ tooltip }) => (
  <span className="info-icon-wrapper" tabIndex={0}>
    <svg className="info-icon" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="10" fill="#bdbdbd"/>
      <text x="10" y="15" textAnchor="middle" fontSize="13" fill="#fff" fontFamily="Arial" fontWeight="bold">i</text>
    </svg>
    <span className="tooltip">{tooltip}</span>
  </span>
);

const ScreenshotForm = ( props ) => {
  const { 
    toggleFullPage, 
    setImageQuality, 
    setViewportWidth, 
    setScreenshotFormat,
    setScreenshotSelector,
    setExcludeSelectors,
    toggleBlockCookieBanners,
    toggleDarkMode,
    screenshotOptions 
  } = props;

  const { 
    imageQuality, 
    viewportWidth, 
    fullPage,
    format,
    selector,
    excludeSelectors,
    blockCookieBanners,
    darkMode
  } = screenshotOptions;

  return (
    <Form className="flat-screenshot-form">
      <div className="form-section">
        <div className="section-label">Viewport</div>
        <RadioButtonGroup
          options={[
            { label: 'Mobile', value: 'xs' },
            { label: 'Tablet', value: 'sm' },
            { label: 'Desktop', value: 'md' },
            { label: 'Full HD', value: 'lg' }
          ]}
          value={viewportWidth}
          onChange={setViewportWidth}
        />
      </div>

      <div className="form-section">
        <div className="section-label">File Type</div>
        <RadioButtonGroup
          options={[
            { label: 'PNG', value: 'png' },
            { label: 'JPG', value: 'jpeg' },
            { label: 'WEBP', value: 'webp' }
          ]}
          value={format}
          onChange={setScreenshotFormat}
        />
      </div>

      <div className="form-section">
        <div className="section-label">Quality</div>
        <div className="quality-slider-container">
          <input
            type="range"
            min="10"
            max="80"
            step="10"
            value={imageQuality}
            onChange={(e) => setImageQuality(e.target.value)}
            className="quality-slider"
          />
          <div className="quality-value-display">{imageQuality}</div>
        </div>
      </div>

      <div className="form-section">
        <div className="section-label">Advanced</div>
        <ToggleSwitch
          id="block-cookie-banners-checkbox"
          checked={blockCookieBanners}
          onChange={toggleBlockCookieBanners}
          label="Block Cookies Banner"
        />
        <ToggleSwitch
                id="full-page-checkbox"
          checked={fullPage}
                onChange={toggleFullPage}
          label="Full Page"
        />
        <ToggleSwitch
          id="dark-mode-checkbox"
          checked={darkMode}
          onChange={toggleDarkMode}
          label="Dark Mode"
        />
        <FormGroup className="mt-2 mb-1 selector-group input-icon-group">
          <div className="input-icon-container">
            <Input
              id="screenshot-selector"
              type="text"
              placeholder="CSS Selector (e.g. #main-content)"
              value={selector || ''}
              onChange={(e) => setScreenshotSelector(e.target.value)}
              bsSize="sm"
            />
            <InfoIcon tooltip="Capture only a specific part of the page in the screenshot by providing a CSS selector (e.g., #product-image or .main-content)." />
          </div>
        </FormGroup>
        <FormGroup className="mb-1 exclude-group input-icon-group">
          <div className="input-icon-container">
                <Input
              id="exclude-selectors"
              type="text"
              placeholder="Exclude (e.g. .ad, .cookie-banner)"
              value={excludeSelectors ? excludeSelectors.join(', ') : ''}
              onChange={(e) => setExcludeSelectors(e.target.value.split(',').map(s => s.trim()))}
              bsSize="sm"
            />
            <InfoIcon tooltip="Hide specific elements from the screenshot—like cookie banners, ads, or popups—by providing their CSS selectors." />
          </div>
          </FormGroup>
      </div>

      <style jsx>{`
        .flat-screenshot-form {
          max-width: 420px;
          margin: 0;
          background: none;
          border-radius: 0;
          box-shadow: none;
          padding: 0;
          font-family: 'Nunito Sans', sans-serif;
        }
        .form-section {
          margin-bottom: 1.2rem;
          padding: 0;
          border: none;
          font-family: 'Nunito Sans', sans-serif;
        }
        .section-label {
          font-size: 1.08rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #222;
          font-family: 'Nunito Sans', sans-serif;
        }
        .radio-group {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          font-family: 'Nunito Sans', sans-serif;
        }
        .radio-btn {
          border: none;
          background: #f0f2f5;
          color: #333;
          padding: 0.4rem 1.1rem;
          border-radius: 20px;
          font-size: 1.05em;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
          outline: none;
          font-family: 'Nunito Sans', sans-serif;
        }
        .radio-btn.selected {
          background: #22c55e;
          color: #fff;
        }
        .radio-btn:not(.disabled):hover,
        .radio-btn:not(.disabled):focus {
          background: #16a34a;
          color: #fff;
        }
        .radio-btn.disabled {
          background: #eaeaea;
          color: #aaa;
          cursor: not-allowed;
        }
        .toggle-row {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          margin-bottom: 0.5rem;
          font-size: 1.08em;
          font-family: 'Nunito Sans', sans-serif;
        }
        .toggle-label {
          min-width: 160px;
          margin-right: 1.2rem;
          font-size: 1.08em;
          font-family: 'Nunito Sans', sans-serif;
        }
        .switch {
          position: relative;
          display: inline-block;
          width: 36px;
          height: 20px;
        }
        .switch input { display: none; }
        .slider {
          position: absolute;
          cursor: pointer;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: #e4e7ef;
          transition: .2s;
          border-radius: 20px;
        }
        .slider:before {
          position: absolute;
          content: "";
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .2s;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }
        input:checked + .slider {
          background-color: #22c55e;
        }
        input:checked + .slider:before {
          transform: translateX(16px);
        }
        .mt-2 { margin-top: 0.7rem; }
        .mb-1 { margin-bottom: 0.5rem; }
        .input-icon-group {
          position: relative;
        }
        .input-icon-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        .input-icon-container .form-control {
          padding-right: 2.2rem;
        }
        .info-icon-wrapper {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          cursor: pointer;
          z-index: 2;
        }
        .info-icon {
          vertical-align: middle;
          margin-left: 2px;
        }
        .info-icon-wrapper:focus .tooltip,
        .info-icon-wrapper:hover .tooltip {
          opacity: 1;
          pointer-events: auto;
        }
        .tooltip {
          opacity: 0;
          pointer-events: none;
          position: absolute;
          left: 28px;
          top: 50%;
          transform: translateY(-50%);
          background: #222;
          color: #fff;
          padding: 0.5em 0.8em;
          border-radius: 6px;
          font-size: 0.85em;
          white-space: pre-line;
          z-index: 10;
          min-width: 220px;
          max-width: 320px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: opacity 0.15s;
          font-family: 'Nunito Sans', sans-serif;
        }
        .quality-slider-container {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-family: 'Nunito Sans', sans-serif;
        }
        .quality-slider {
          flex: 1;
          height: 6px;
          border-radius: 5px;
          background: #e4e7ef;
          outline: none;
          -webkit-appearance: none;
          appearance: none;
        }
        .quality-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #22c55e;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .quality-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #22c55e;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .quality-value-display {
          min-width: 2.5rem;
          text-align: center;
          font-weight: 600;
          font-size: 1.05em;
          color: #22c55e;
          font-family: 'Nunito Sans', sans-serif;
        }
      `}</style>
    </Form>
  );
};

const mapStateToProps = (state) => ({
  screenshotOptions: state.debuggerForm.screenshot.options,
})

const mapDispatchToProps = (dispatch) => ({
  setViewportWidth: (viewportWidth) => dispatch(setViewportWidth(viewportWidth)),
  setImageQuality: (imageQuality) => dispatch(setImageQuality(imageQuality)),
  toggleFullPage: () => dispatch(toggleFullPage()),
  setScreenshotFormat: (format) => dispatch(setScreenshotFormat(format)),
  setScreenshotSelector: (selector) => dispatch(setScreenshotSelector(selector)),
  setExcludeSelectors: (selectors) => dispatch(setExcludeSelectors(selectors)),
  toggleBlockCookieBanners: () => dispatch(toggleBlockCookieBanners()),
  toggleDarkMode: () => dispatch(toggleDarkMode()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ScreenshotForm)


